import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { safeNext } from "../lib/oauthConsent";
import { useAuth } from "../contexts/AuthContext";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = safeNext(location.state?.from) || "/apps";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(destination, { replace: true });
  }, [user, navigate, destination]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Connexion réussie");
      navigate(destination, { replace: true });
    } else {
      toast.error(r.error);
    }
  };

  return (
    <AuthLayout
      kicker="Simplifier aujourd'hui, une meilleure santé demain"
      headlineLines={["Plus de pharmacies,", "plus d'opportunités."]}
      quote="Au service d'une meilleure santé en Afrique."
    >
      <div className="text-center mb-8">
        <h2 className="font-serif-display text-3xl font-bold text-slate-900">Bon retour !</h2>
        <p className="text-sm text-slate-500 mt-2">
          Connectez-vous à votre espace Jamora CRM pour continuer.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Adresse e-mail</Label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="email"
              data-testid="input-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              className="pl-10 h-11"
            />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Mot de passe</Label>
            <Link
              to="/forgot-password"
              data-testid="link-forgot-password"
              className="text-xs font-medium text-jamora hover:underline"
            >
              Mot de passe oublié ?
            </Link>
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="password"
              data-testid="input-login-password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Votre mot de passe"
              className="pl-10 pr-10 h-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          data-testid="btn-login-submit"
          disabled={loading}
          className="w-full h-11"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : "Se connecter"}
          {!loading && <ArrowRight size={16} />}
        </Button>
      </form>
    </AuthLayout>
  );
}
